// HILDR — Utvikling til Strid (UtS): Hærens plan- og beslutningsprosess (HPBP)
// for kompani/eskadron, som datamodell + fremdriftskontroll.
//
// KILDE (verifisert, ikke gjengitt fra hukommelse): «Håndbok for Plan og
// beslutningsprosess for kompani og eskadron» (Hæren), slik den er gjengitt i
// Melgård, Ø. (2013): «Beslutningsfeller i planprosessen», Krigsskolen, Figur 3
// og kap. 3. To sitater styrer modellen her:
//
//   Trinn 2: «Disse faste variablene er i kronologisk rekkefølge oppdraget,
//   været, lendet, fienden, egne ressurser, tid, sivile forhold og risiko.»
//
// ⚠ BEVISST AVVIK FRA SITATET OVER — LES DETTE FØR DU «RETTER» KODEN:
// rekkefølgen i `FASTE_VARIABLER` følger IKKE sitatet lenger. Den følger NATO
// METT-TC(R): oppdrag, fiende, lende (med vær), egne, tid, sivile, risiko.
// Endringen er bestilt av eier (fagansvarlig HV) med begrunnelsen «Ved å bruke
// den rekkefølgen er vi tro til nato METTTCR».
//
// Avviket er reelt og står her fordi det ikke skal oppdages som en feil senere:
// den norske håndboka setter været som variabel nr. 2, mens METT-TC har ÉN T som
// dekker terreng OG vær. Vi har valgt NATO-formen, og været er derfor blitt en
// del av lendevurderingen istedenfor en variabel ved siden av den. Sitatet står
// uendret over fordi kilden ikke skal omskrives for å passe koden.
//
//   Trinn 3: «…utvikle to til tre prinsipielt forskjellige handlemåter […] En
//   vurdering av tid og risiko skal ligge til grunn for om man velger å utvikle
//   kun en handlemåte eller flere. Det skisseres to metoder […]; helhetlig og
//   analytisk.»
//
// HVORFOR DETTE ER KJERNEN: en fempunktsordre er PRODUKTET av prosessen. Uten
// UtS er ordremotoren bare en mal. Denne modulen gjør prosessen sporbar: hva er
// gjort, hva mangler, og hvilke delprodukter finnes — slik at en stab kan se
// hvor de er, ikke bare hva de skrev til slutt.
//
// Modulen er REN og deterministisk. Den fyller ikke ut faglige vurderinger
// automatisk (det ville vært å dikte etterretning), men den vet HVA som skal
// vurderes, i hvilken rekkefølge, og hva som mangler.
import { buildDtg } from '@skade/contracts';
export const TRINN2_DELPRODUKT = [
    // 1. Oppdraget
    { variabel: 'oppdrag', delprodukt: 'Oppdrag til egen avdeling nedskrevet', kritisk: true },
    { variabel: 'oppdrag', delprodukt: 'Bindinger og begrensninger uthentet', kritisk: false },
    { variabel: 'oppdrag', delprodukt: 'Reformulert oppdrag', kritisk: true },
    // 2. Fienden
    { variabel: 'fiende', delprodukt: 'Delkonklusjon fienden', kritisk: true },
    { variabel: 'fiende', delprodukt: 'Innhentings-/rekognoseringsplan', kritisk: false },
    // 3. Lendet — med været, jf. METT-TC sin ene T for terreng og vær
    { variabel: 'lende', delprodukt: 'Værets påvirkning på lendet', kritisk: true },
    { variabel: 'lende', delprodukt: 'Delkonklusjon lende', kritisk: true },
    { variabel: 'lende', delprodukt: 'Lendekalk', kritisk: false },
    // 4. Egne ressurser. Støttebehovet hører HIT og ikke løsrevet til slutt: det er
    //    utledet av hva vi selv mangler, altså av egne ressurser.
    { variabel: 'egneRessurser', delprodukt: 'Egne ressurser analysert', kritisk: true },
    { variabel: 'egneRessurser', delprodukt: 'Støttebehov', kritisk: false },
    // 5. Tid
    { variabel: 'tid', delprodukt: 'Tidsanalyse', kritisk: true },
    // 6. Sivile forhold
    { variabel: 'sivileForhold', delprodukt: 'Sivile forhold analysert', kritisk: true },
    // 7. Risiko — to vurderinger, ikke én. Taktisk risiko er risikoen for at
    //    oppdraget ikke løses; ulykkesrisiko er tap UTEN fiendtlig påvirkning. De
    //    besvares ulikt og skal derfor stå som to delprodukter.
    { variabel: 'risiko', delprodukt: 'Taktisk risiko vurdert', kritisk: true },
    { variabel: 'risiko', delprodukt: 'Ulykkesrisiko vurdert', kritisk: true },
];
/** Delproduktene for én variabel — til UI-et, som viser dem under variabelen. */
export function delprodukterFor(v) {
    return TRINN2_DELPRODUKT.filter((d) => d.variabel === v);
}
export const UTS_TRINN = [
    {
        nr: 1, navn: 'Mottak av oppdrag',
        hensikt: 'Skape forståelse for tildelt oppdrag, synkronisere egen forståelse med '
            + 'overordnedes intensjon, vurdere tiden fram til iverksettelse og varsle egen avdeling.',
        delprodukter: ['Varslingsordre 1 (VO 1)'],
    },
    {
        nr: 2, navn: 'Oppdragsanalyse',
        hensikt: 'Analytisk fase der de faste variablene analyseres i METT-TC(R)-rekkefølge.',
        // UTLEDES av TRINN2_DELPRODUKT — se den. Rekkefølgen kan derfor ikke drifte fra
        // variabelrekkefølgen, og en test håndhever det.
        delprodukter: TRINN2_DELPRODUKT.map((d) => d.delprodukt),
    },
    {
        nr: 3, navn: 'Utvikling av handlemåter',
        hensikt: 'Utvikle to til tre prinsipielt forskjellige handlemåter og velge hvilken '
            + 'oppdragsløsningen skal bygge på. Tid og risiko avgjør om én eller flere utvikles.',
        delprodukter: ['Handlemåter', 'Valgt handlemåte med plan', 'Varslingsordre 2 (VO 2)'],
    },
    {
        nr: 4, navn: 'Utvikling av planen',
        hensikt: 'Iverksette rekognosering og informasjonsinnhenting, ferdigstille plan og '
            + 'ordre på grunnlag av valgt handlemåte, og holde ordre for underlagte.',
        delprodukter: ['Rekognoseringsresultat', 'Ferdig plan', 'Ordre (fempunktsordre)'],
    },
    {
        nr: 5, navn: 'Gjennomgang og kontroll av planen',
        hensikt: 'Tilbakelesning av oppdraget fra undergitte sjefer, koordinering av planen '
            + 'og eventuelle justeringer.',
        delprodukter: ['Tilbakelesning', 'Koordinering', 'Justeringer'],
    },
];
// ── Trinn 2: de faste variablene, i doktrinær rekkefølge ────────────────────
/**
 * Nøkler for de faste variablene, i doktrinær rekkefølge. Rekkefølgen ER
 * doktrinen — ikke sorter om.
 *
 * REKKEFØLGEN FØLGER NATO METT-TC(R), bekreftet av eier:
 *   M  Mission            → oppdrag
 *   E  Enemy              → fiende
 *   T  Terrain & weather  → lende  (VÆRET LIGGER UNDER LENDE, se under)
 *   T  Troops available   → egneRessurser
 *   T  Time available     → tid
 *   C  Civil considerations → sivileForhold
 *   R  Risk               → risiko
 *
 * HVORFOR VÆRET IKKE LENGER ER EN EGEN VARIABEL:
 * det var det før, som nr. 2 av åtte. Men i METT-TC dekker ÉN T både terreng og
 * vær, og det er faglig riktig: været betyr noe for planen bare gjennom hva det
 * gjør med lendet og med sikten i lendet. En sti som er farbar i tørrvær og
 * ufremkommelig etter regn er én vurdering, ikke to. Å ha dem som sidestilte
 * variabler inviterte til å skrive «regn ventes» på ett sted og «myrlendt» på et
 * annet, uten at noen koblet dem.
 *
 * Været er derfor nå en DEL av lendevurderingen (`VAERFAKTORER` under `KAVOD`),
 * ikke en variabel ved siden av den.
 */
export const FASTE_VARIABLER = [
    'oppdrag', 'fiende', 'lende', 'egneRessurser', 'tid', 'sivileForhold', 'risiko',
];
export const FAST_VARIABEL_NAVN = {
    oppdrag: 'Oppdraget',
    fiende: 'Fienden',
    lende: 'Lendet (med vær)',
    egneRessurser: 'Egne ressurser',
    tid: 'Tid',
    sivileForhold: 'Sivile forhold',
    risiko: 'Risiko',
};
/** METT-TC(R)-bokstaven hver variabel svarer til. Vises i UI som doktrinært anker. */
export const METTCR_BOKSTAV = {
    oppdrag: 'M',
    fiende: 'E',
    lende: 'T',
    egneRessurser: 'T',
    tid: 'T',
    sivileForhold: 'C',
    risiko: 'R',
};
export const VTSSN = [
    { kode: 'V', navn: 'Vær', sporsmal: 'Hvilket vær er ventet i operasjonsperioden, og hvordan slår det inn?', fraMet: 'symbolNext1h' },
    { kode: 'T', navn: 'Temperatur', sporsmal: 'Hvordan påvirker temperaturen personell, materiell og utholdenhet?', fraMet: 'airTemperature' },
    { kode: 'S', navn: 'Sikt', sporsmal: 'Hvor langt ser vi — og når endres det (tussmørke, tåke, nedbør)?', fraMet: 'symbolNext1h' },
    { kode: 'S', navn: 'Skydekke', sporsmal: 'Hvordan påvirker skydekket lysforhold, NVG-ytelse og luftstøtte?', fraMet: 'cloudAreaFraction' },
    { kode: 'N', navn: 'Nedbør', sporsmal: 'Hvordan påvirker nedbøren fremkommelighet, sikt og materiell?', fraMet: 'precipitationNext1h' },
];
/**
 * VIND — lagt til på eiers bestilling: «Legg til under vær: vind. Styrke og retning.»
 *
 * HVORFOR DEN IKKE ER EN BOKSTAV I VTSSN: mnemonikken er fem bokstaver, bekreftet
 * av fagansvarlig, og den skal ikke skrives om her. Men vind mangler i den, og det
 * er en reell mangel — vinden er den værfaktoren som påvirker FLEST andre ting i en
 * plan: lyddekning (en vind på 8 m/s dekker lyden av en tropp i bevegelse),
 * røyk/CBRN-drift, drone- og helikopteroperasjoner, kuleavdrift, og kuldeindeks som
 * er noe annet enn temperaturen alene.
 *
 * Retningen oppgis som «FRA», altså meteorologisk konvensjon, samme som MET-feltet
 * `wind_from_direction`. Det er en klassisk feilkilde: en operatør som leser «vind
 * 270°» og tror det betyr «vinden blåser mot vest» får lyddekningen speilvendt.
 * Derfor står «fra» i teksten, ikke bare i kommentaren.
 */
export const VAERFAKTOR_VIND = {
    kode: 'Vind',
    navn: 'Vind (styrke og retning)',
    sporsmal: 'Hvor sterk er vinden, og hvilken retning kommer den FRA? Vurder lyddekning, '
        + 'røyk-/CBRN-drift, drone- og luftoperasjoner, og kuldeindeks.',
    fraMet: 'windSpeed + windFromDirection',
};
/**
 * Alle værfaktorene som skal vurderes under LENDE. Dette er den kanoniske lista
 * som UI-et skal bruke; `VTSSN` beholdes uendret som mnemonikken den er.
 */
export const VAERFAKTORER = [...VTSSN, VAERFAKTOR_VIND];
/**
 * Kompassretning fra grader, 8 sektorer. Brukes til å skrive vindretning slik den
 * SIES over samband («fra nordvest») og ikke bare som et tall.
 */
export function vindretningNavn(fraGrader) {
    const d = ((fraGrader % 360) + 360) % 360;
    const navn = ['nord', 'nordøst', 'øst', 'sørøst', 'sør', 'sørvest', 'vest', 'nordvest'];
    return navn[Math.round(d / 45) % 8];
}
/**
 * Vindstyrke etter Beaufort, med den operative konsekvensen som betyr noe for en
 * plan. Tersklene er Beaufort-skalaens egne (m/s); konsekvensteksten er vår.
 *
 * MERK om lyddekning: at vind demper lyd oppover og bærer den nedover er reelt,
 * men HVOR MYE er terreng- og temperaturavhengig og er IKKE tallfestet her. Teksten
 * sier derfor «kan dekke», ikke «dekker». Å gi en meterverdi ville vært å dikte.
 */
export function vindstyrke(ms) {
    const trinn = [
        [0.3, 0, 'Stille', 'Ingen lyddekning — bevegelse høres langt.'],
        [1.6, 1, 'Flau vind', 'Ingen praktisk lyddekning.'],
        [3.4, 2, 'Svak vind', 'Så vidt merkbar lyddekning.'],
        [5.5, 3, 'Lett bris', 'Kan dekke lyd av bevegelse på kort hold. Røyk driver merkbart.'],
        [8.0, 4, 'Laber bris', 'Kan dekke lyd av tropp i bevegelse. Små droner merker vinden.'],
        [10.8, 5, 'Frisk bris', 'God lyddekning. Små droner er nær grensen.'],
        [13.9, 6, 'Liten kuling', 'Små droner bør ikke flys. Merkbar kuldeindeks.'],
        [17.2, 7, 'Stiv kuling', 'Droneoperasjoner ute. Presisjonsskyting påvirkes.'],
        [20.8, 8, 'Sterk kuling', 'Tung påvirkning på personell og materiell.'],
        [Infinity, 9, 'Sterk storm eller mer', 'Operasjonell begrensning i seg selv.'],
    ];
    for (const [grense, b, navn, konsekvens] of trinn) {
        if (ms < grense)
            return { beaufort: b, navn, konsekvens };
    }
    return { beaufort: 12, navn: 'Orkan', konsekvens: 'Operasjonell begrensning i seg selv.' };
}
export const KAVOD = [
    {
        kode: 'K', navn: 'Kunstige og naturlige hindre',
        sporsmal: 'Hva i terrenget hindrer eller kanaliserer bevegelse?',
        terrengStotte: 'Helning/bratthet og vassdrag fra DEM; bygninger og kraftlinjer fra OSM-infralaget.',
    },
    {
        kode: 'A', navn: 'Adkomstveier',
        sporsmal: 'Hvilke akser kan en styrke bevege seg langs inn i og gjennom området — '
            + 'til fots, på hjul og på belter? Hvor kanaliseres de, og hvor kan de sperres?',
        terrengStotte: 'Fremkommelighet kan vurderes mot helning og hindre fra DEM/OSM, men hvilken akse '
            + 'som faktisk VELGES er taktisk skjønn.',
    },
    {
        kode: 'V', navn: 'Viktig lende',
        sporsmal: 'Hvilket lende gir den som holder det en avgjørende fordel? '
            + 'Merk: viktig lende er ikke det høyeste punktet — det er det punktet som gir kontroll.',
        terrengStotte: 'Kandidater kan rangeres på hvor mye de dekker (viewshed) og på hvilke '
            + 'adkomstveier de behersker. Utvalget er taktisk skjønn.',
    },
    {
        kode: 'O', navn: 'Observasjon og skuddfelt',
        sporsmal: 'Hvor langt kan du se, og hvor kan du skyte uhindret?',
        terrengStotte: 'Regnes direkte: sektor-viewshed og siktelinje med jordkrumming (@skade/terrain).',
    },
    {
        kode: 'D', navn: 'Dekning og skjul',
        sporsmal: 'Hva skjuler deg mot fiendtlig observasjon (skjul) og mot ild (dekning)?',
        terrengStotte: 'Dødvinkler = komplementet av fiendens viewshed; terrengskygge gir skjul mot optisk observasjon.',
    },
];
/**
 * ANGREP OG FORSVAR ER ET PERSPEKTIV PÅ ALLE FEM FAKTORENE — IKKE TO AV DEM.
 *
 * Eier-presisering, ordrett: «K kunstige og naturlige hindre / A adkomstveier /
 * V viktig lende / O observasjon og skuddfelt / D dekning og skjul. Dette
 * vurderes både i angrep og forsvar.»
 *
 * DETTE VAR EN REELL FEIL I MODELLEN, og den var verre enn et navnebytte: A og V
 * var kodet som «Vurderinger i angrep» og «Vurderinger i forsvar». Følgen var at
 * verktøyet ALDRI spurte om adkomstveier eller viktig lende — to av de mest
 * avgjørende lendefaktorene som finnes. Samtidig ble angrep/forsvar-dimensjonen
 * gjemt inne i to bokstaver, så de tre andre faktorene ble bare vurdert én gang,
 * uten å si fra hvilket ståsted.
 *
 * Hvorfor forskjellen er stor i praksis: samme bekk er et HINDER for den som
 * angriper og en FLANKESIKRING for den som forsvarer. Samme høyde gir skuddfelt
 * for forsvareren og er dødt lende for angriperen. Én rute per faktor tvinger
 * staben til å velge ett av svarene og glemme det andre — og det som glemmes er
 * nesten alltid motpartens.
 *
 * KAVOD ≡ NATO OAKOC/OCOKA, faktor for faktor:
 *   O Observation and fields of fire → O
 *   C Cover and concealment         → D
 *   O Obstacles                     → K
 *   K Key terrain                   → V
 *   A Avenues of approach           → A
 * Samme grep som METT-TC-rekkefølgen over: vi er tro til NATO-formen.
 */
export const LENDE_PERSPEKTIV = ['angrep', 'forsvar'];
export const LENDE_PERSPEKTIV_NAVN = {
    angrep: 'I angrep',
    forsvar: 'I forsvar',
};
/** IPB-trinnene, fordelt på vær/lende (1-2) og fiende (3-4) som i Figur 3. */
export const IPB_TRINN = [
    { nr: 1, navn: 'Definér operasjonsmiljøet', underVariabel: 'lende' },
    { nr: 2, navn: 'Beskriv operasjonsmiljøets virkning', underVariabel: 'lende' },
    { nr: 3, navn: 'Evaluer trusselen', underVariabel: 'fiende' },
    { nr: 4, navn: 'Utvikle fiendtlige handlemåter', underVariabel: 'fiende' },
];
export const OPPGAVE_TYPE_NAVN = {
    spesifisert: 'Spesifisert oppgave (står i ordren)',
    utledet: 'Utledet oppgave (følger av ordren)',
    avgjorende: 'Avgjørende oppgave (må lykkes)',
};
/**
 * Er tidsplanen komplett nok til å regne fordeling? Brukes som type-vakt, slik
 * at ingen kaller `fordelTid()` på en halvferdig plan og får NaN-minutter ut.
 */
export function tidsplanKomplett(t) {
    return t?.mottattTsMs != null && t.iverksettTsMs != null;
}
/** Tom prosess — alt uvurdert. Startpunktet for en ny planprosess. */
export function nyPlanprosess(id) {
    return {
        id, trinn: 1,
        ordreForstatt: false, tilbakelest: false, egenEvneVurdert: false,
        analyse: {
            begrensninger: [], bindinger: [], oppgaver: [],
            vaer: { faktorer: {} },
            lende: { faktorer: {} },
            fiende: { handlemater: [], etterretningsbehov: [] },
            risiko: { tiltak: [] },
            stottebehov: [],
        },
        handlemater: [],
        rekognoseringGjennomfort: false, planFerdigstilt: false,
        tilbakelesningFraUndergitte: false, koordinert: false, justeringer: [],
    };
}
/**
 * Hva mangler for at et trinn er fullført? Dette er poenget med en planprosess-
 * modell: den skal si HVA som ikke er gjort, ikke bare vise en tom mal.
 * «Kritisk» betyr at trinnet ikke kan regnes som gjennomført uten det.
 */
export function manglerForTrinn(p, trinn) {
    const m = [];
    const a = p.analyse;
    if (trinn === 1) {
        if (!p.ordreForstatt)
            m.push({ trinn, hva: 'Ordren er ikke lest og forstått', kritisk: true });
        if (!p.tilbakelest)
            m.push({ trinn, hva: 'Oppdraget er ikke lest tilbake til overordnet', kritisk: true });
        // En halvferdig tidsplan er lagret, men er ikke levert. Si HVA som står igjen
        // — «tidsplan mangler» er misvisende når operatøren nettopp fylte ut halve.
        if (!tidsplanKomplett(p.tidsplan)) {
            const halv = p.tidsplan?.mottattTsMs != null || p.tidsplan?.iverksettTsMs != null;
            m.push({
                trinn,
                hva: halv
                    ? (p.tidsplan?.mottattTsMs == null
                        ? 'Tidsplan er halvferdig — mottakstidspunkt mangler'
                        : 'Tidsplan er halvferdig — H-time mangler')
                    : 'Foreløpig tidsplan mangler',
                kritisk: true,
            });
        }
        if (!p.egenEvneVurdert)
            m.push({ trinn, hva: 'Egen avdelings evne til å løse oppdraget er ikke vurdert', kritisk: true });
        if (!p.vo1SendtTsMs)
            m.push({ trinn, hva: 'Varslingsordre 1 er ikke sendt', kritisk: false });
        return m;
    }
    if (trinn === 2) {
        if (!a.hensikt2NivaOpp)
            m.push({ trinn, variabel: 'oppdrag', hva: 'Hensikten to nivå opp er ikke fastslått', kritisk: true });
        if (!a.oppdrag2NivaOpp)
            m.push({ trinn, variabel: 'oppdrag', hva: 'Oppdraget to nivå opp er ikke fastslått', kritisk: true });
        // Originalordlyden FØRST. Uten den kan ingen kontrollere om reformuleringen
        // nedenfor tolket oppdraget eller endret det.
        if (!a.oppdragTilEgenAvdeling)
            m.push({ trinn, variabel: 'oppdrag', hva: 'Oppdraget til egen avdeling er ikke nedskrevet ordrett (delprodukt)', kritisk: true });
        if (!a.egenHensikt)
            m.push({ trinn, variabel: 'oppdrag', hva: 'Egen hensikt mangler', kritisk: true });
        if (!a.oppgaver.some((o) => o.type === 'spesifisert'))
            m.push({ trinn, variabel: 'oppdrag', hva: 'Ingen spesifiserte oppgaver er hentet ut av ordren', kritisk: true });
        if (!a.oppgaver.some((o) => o.type === 'avgjorende'))
            m.push({ trinn, variabel: 'oppdrag', hva: 'Avgjørende oppgave(r) er ikke identifisert', kritisk: true });
        if (!a.reformulertOppdrag)
            m.push({ trinn, variabel: 'oppdrag', hva: 'Reformulert oppdrag mangler (delprodukt)', kritisk: true });
        // FIENDEN FØR LENDET — METT-TC(R)-rekkefølgen. Rekkefølgen her styrer
        // rekkefølgen operatøren ser manglene i, så den skal være den doktrinære.
        if (!a.fiende.trussel)
            m.push({ trinn, variabel: 'fiende', hva: 'Trusselen er ikke evaluert (IPB trinn 3)', kritisk: true });
        if (!a.fiende.handlemater.length)
            m.push({ trinn, variabel: 'fiende', hva: 'Fiendtlige handlemåter er ikke utviklet (IPB trinn 4)', kritisk: true });
        if (!a.fiende.delkonklusjon)
            m.push({ trinn, variabel: 'fiende', hva: 'Delkonklusjon fienden mangler (delprodukt)', kritisk: true });
        if (!a.fiende.etterretningsbehov.length)
            m.push({ trinn, variabel: 'fiende', hva: 'Foreløpig etterretningsbehov / innhentingsplan mangler (delprodukt)', kritisk: false });
        // Været er en DEL av lendevurderingen, ikke en variabel ved siden av den —
        // se kommentaren over FASTE_VARIABLER. Mangelen føres derfor på `lende`, så
        // operatøren finner den der han faktisk jobber med den.
        if (!a.vaer.paavirkning)
            m.push({ trinn, variabel: 'lende', hva: 'Værets påvirkning på lendet er ikke konkludert (delprodukt)', kritisk: true });
        if (!a.lende.delkonklusjon)
            m.push({ trinn, variabel: 'lende', hva: 'Delkonklusjon lende mangler (delprodukt)', kritisk: true });
        if (!a.lende.lendekalkRef)
            m.push({ trinn, variabel: 'lende', hva: 'Lendekalk er ikke laget (delprodukt)', kritisk: false });
        if (!a.egneRessurser)
            m.push({ trinn, variabel: 'egneRessurser', hva: 'Egne ressurser er ikke analysert', kritisk: true });
        if (!a.tid)
            m.push({ trinn, variabel: 'tid', hva: 'Tidsanalyse mangler', kritisk: true });
        if (!a.sivileForhold)
            m.push({ trinn, variabel: 'sivileForhold', hva: 'Sivile forhold (folk, infrastruktur) er ikke analysert', kritisk: true });
        if (!a.risiko.taktisk)
            m.push({ trinn, variabel: 'risiko', hva: 'Taktisk risiko er ikke vurdert', kritisk: true });
        if (!a.risiko.ulykke)
            m.push({ trinn, variabel: 'risiko', hva: 'Ulykkesrisiko er ikke vurdert', kritisk: true });
        return m;
    }
    if (trinn === 3) {
        if (!p.hmMetode)
            m.push({ trinn, hva: 'Metode for utvikling av handlemåter er ikke valgt (helhetlig eller analytisk)', kritisk: false });
        if (!p.handlemater.length)
            m.push({ trinn, hva: 'Ingen handlemåter er utviklet', kritisk: true });
        for (const h of p.handlemater) {
            if (!h.krigsspill)
                m.push({ trinn, hva: `Handlemåte «${h.navn}» er ikke krigsspilt`, kritisk: true });
        }
        if (p.handlemater.length > 1 && !p.valgtHmId)
            m.push({ trinn, hva: 'Handlemåte er ikke valgt', kritisk: true });
        if (p.handlemater.length === 1)
            m.push({ trinn, hva: 'Bare ÉN handlemåte utviklet — håndboka forutsetter to til tre prinsipielt forskjellige, med mindre tid/risiko begrunner én', kritisk: false });
        return m;
    }
    if (trinn === 4) {
        if (!p.rekognoseringGjennomfort)
            m.push({ trinn, hva: 'Rekognosering og oppklaring er ikke gjennomført', kritisk: true });
        if (!p.planFerdigstilt)
            m.push({ trinn, hva: 'Planen er ikke ferdigstilt', kritisk: true });
        if (!p.ordreGittTsMs)
            m.push({ trinn, hva: 'Ordre er ikke gitt', kritisk: true });
        return m;
    }
    if (!p.tilbakelesningFraUndergitte)
        m.push({ trinn: 5, hva: 'Tilbakelesning fra undergitte sjefer mangler', kritisk: true });
    if (!p.koordinert)
        m.push({ trinn: 5, hva: 'Planen er ikke koordinert', kritisk: true });
    return m;
}
/** Alle mangler i hele prosessen, i trinnrekkefølge. */
export function alleMangler(p) {
    return [1, 2, 3, 4, 5].flatMap((t) => manglerForTrinn(p, t));
}
/** Er trinnet gjennomført? (ingen KRITISKE mangler igjen) */
export function trinnFullfort(p, trinn) {
    return !manglerForTrinn(p, trinn).some((m) => m.kritisk);
}
/** Første trinn som ikke er fullført — der staben faktisk står. */
export function forsteApneTrinn(p) {
    for (const t of [1, 2, 3, 4, 5])
        if (!trinnFullfort(p, t))
            return t;
    return null;
}
export function fremdrift(p) {
    const fullforte = [1, 2, 3, 4, 5].filter((t) => trinnFullfort(p, t));
    const apent = forsteApneTrinn(p);
    const kritiske = alleMangler(p).filter((m) => m.kritisk);
    return {
        trinn: apent ?? 5,
        andel: fullforte.length / 5,
        fullforte,
        kritiskeMangler: kritiske.length,
        nesteHandling: kritiske[0]?.hva ?? null,
    };
}
/**
 * Fordel tilgjengelig tid mellom egen planlegging og underavdelingene.
 *
 * FORBEHOLD: tredelsregelen (behold 1/3, gi 2/3 videre) er utbredt praksis i
 * NATO-planlegging, men den er IKKE utdypet i kildedokumentet vi har verifisert.
 * Standardandelen er derfor overstyrbar, og at den er en TUMMELREGEL står i
 * returverdien — den skal ikke fremstå som en doktrinær grense.
 */
export function fordelTid(t, egenAndel = 1 / 3) {
    const totaltMin = Math.max(0, Math.round((t.iverksettTsMs - t.mottattTsMs) / 60_000));
    const andel = Math.min(1, Math.max(0, egenAndel));
    const egen = Math.round(totaltMin * andel);
    return {
        totaltMin,
        egenPlanleggingMin: egen,
        tilUnderavdelingerMin: totaltMin - egen,
        egenAndel: andel,
    };
}
/**
 * Skal det utvikles én eller flere handlemåter? Håndboka: «En vurdering av tid
 * og risiko skal ligge til grunn». Vi regner derfor, men lar begrunnelsen følge
 * med — dette er en anbefaling til sjefen, ikke en avgjørelse.
 */
export function anbefaltAntallHandlemater(tid, hoyRisiko) {
    if (tid.egenPlanleggingMin < 30) {
        return { antall: 1, begrunnelse: `Bare ${tid.egenPlanleggingMin} min til egen planlegging — utvikle ÉN handlemåte og prioritér å få ut ordren.` };
    }
    if (hoyRisiko && tid.egenPlanleggingMin >= 90) {
        return { antall: 3, begrunnelse: `Høy risiko og ${tid.egenPlanleggingMin} min planleggingstid — utvikle tre prinsipielt forskjellige handlemåter.` };
    }
    return { antall: 2, begrunnelse: `${tid.egenPlanleggingMin} min planleggingstid — utvikle to prinsipielt forskjellige handlemåter.` };
}
/**
 * Bygg varslingsordre. VO 1 sendes i trinn 1 (varsle avdelingen om kommende
 * oppdrag), VO 2 i trinn 3 (etter valg av handlemåte, «hvis store endringer»).
 * Deterministisk: samme input → samme ordre.
 */
export function byggVarslingsordre(nr, p, opts) {
    const a = p.analyse;
    const punkter = [];
    if (nr === 1) {
        punkter.push(a.reformulertOppdrag || a.egenHensikt || 'Oppdrag mottatt — nærmere ordre følger.');
        if (tidsplanKomplett(p.tidsplan)) {
            const f = fordelTid(p.tidsplan);
            punkter.push(`Tid: ${f.totaltMin} min til iverksettelse. Underavdelinger får ${f.tilUnderavdelingerMin} min.`);
        }
        punkter.push('Klargjør materiell og personell. Ingen forflytning før ordre.');
        if (a.fiende.trussel)
            punkter.push(`Foreløpig trusselbilde: ${a.fiende.trussel}`);
    }
    else {
        const valgt = p.handlemater.find((h) => h.id === p.valgtHmId);
        punkter.push(valgt ? `Valgt handlemåte: ${valgt.navn} — ${valgt.beskrivelse}` : 'Handlemåte valgt — plan under ferdigstillelse.');
        if (a.egenHensikt)
            punkter.push(`Hensikt: ${a.egenHensikt}`);
        punkter.push('Forbered i henhold til valgt handlemåte. Full ordre følger.');
    }
    return { nr, dtg: buildDtg(opts.tsMs, opts.sone ?? 'A'), fra: opts.fra, til: opts.til, punkter };
}
/** VO som ren tekst for opplesning på samband. */
export function varslingsordreTilTekst(v) {
    return [
        `VARSLINGSORDRE ${v.nr}`,
        `DTG ${v.dtg} · FRA ${v.fra} · TIL ${v.til}`,
        ...v.punkter.map((p, i) => `${i + 1}. ${p}`),
        'Kvitter.',
    ].join('\n');
}
//# sourceMappingURL=uts.js.map